'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  abortAllPendingGraphQL,
  createApolloClient,
} from '@/publicSearch/apolloClient';
import { ApolloClient } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import SplashScreen from '@/components/SplashScreen/SplashScreen';

function GraphQLProvider({ children }: { children: ReactNode | ReactNode[] }) {
  const [apolloClient] = useState<ApolloClient | null>(() => {
    abortAllPendingGraphQL();
    return createApolloClient({ token: '' });
  });

  const societyId = usePublicSearchContext(
    (ctx) => ctx.society.societyId ?? ctx.society.orgId,
  );
  const prevSocietyIdRef = useRef(societyId);

  useEffect(() => {
    const prev = prevSocietyIdRef.current;
    if (prev === societyId) return;
    prevSocietyIdRef.current = societyId;
    if (!apolloClient) return;
    // Skip the initial empty-→-resolved transition (fresh tab / cleared
    // sessionStorage). At that point the society has just been committed
    // to the store and child queries on the search page may already have
    // started their first fetch — aborting + resetStore here would cancel
    // the first GetAnimalEbvs/etc. request silently (the abortable link
    // converts AbortError into observer.complete() with no value), leaving
    // the lazy query stuck in `loading: true` with no in-flight POST.
    // Only reset when we're transitioning between two resolved societies.
    if (!prev || !societyId) return;
    abortAllPendingGraphQL();
    // clearStore() does not refetch active queries; after aborts complete without
    // data, the UI can stay empty. resetStore() clears and re-runs observables.
    void apolloClient.resetStore().catch((err) => {
      console.warn('[GraphQLProvider] resetStore failed', err);
    });
  }, [societyId, apolloClient]);

  if (!apolloClient) return <SplashScreen />;

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}

export default GraphQLProvider;
